import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import type { User } from '../context/AuthContext';

interface RequireTierProps {
  children: React.ReactElement;
  /** Tiers allowed through. Anything not in this set (canonically 'guest') is redirected. */
  allow: string[];
  /** Where to send a blocked visitor. Defaults to the pricing page (upsell path). */
  redirectTo?: string;
}

/**
 * MSAPLING-PLATFORM-STATS-DASHBOARD-20260824: mirrors AdminRoute's shape for
 * tier-based gating (this repo previously had no route-level tier guard —
 * tier checks were only ever done in-component, which is a silent-degrade
 * pattern, not a real lock). user.tier's canonical values in this codebase
 * are 'guest' | 'free' | 'monthly' | 'lifetime' | 'infra' | 'infra_credits'
 * (confirmed against BillingDashboard.tsx's own tier checks) — 'pro' only
 * exists as a display label in tierConfigStore.ts's config keys, never as a
 * stored tier value. 'infra' is BYOK-only (zero MSapling-funded credits);
 * 'infra_credits' additionally carries a $10/mo included credit grant.
 */
const RequireTier: React.FC<RequireTierProps> = ({ children, allow, redirectTo = '/pricing' }) => {
  const { isLoading, user } = useAuth();
  const tier = (user as User | null)?.tier || 'guest';

  if (isLoading) {
    return <div className="loading-screen">Loading MSapling...</div>;
  }

  if (!user || !allow.includes(tier)) {
    return <Navigate to={redirectTo} replace />;
  }

  return children;
};

export default RequireTier;
